import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Waves, Anchor, Compass, Fish, Droplets, Ship, Map, Wind } from 'lucide-react';

const OceanographyResearch: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Deep Blue Institute";
    
    return (
        <div className="min-h-screen bg-[#001427] font-sans text-white selection:bg-[#708d81] selection:text-white relative overflow-hidden">
            {/* Depth Gradient Background */}
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#003049] via-[#001427] to-[#000814]"></div>

            {/* Bubble Particles (CSS - Simplified) */}
            <div className="absolute inset-0 z-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

            {/* Navbar */}
            <nav className="relative z-50 flex justify-between items-center px-8 py-6 border-b border-white/5 bg-[#003049]/50 backdrop-blur-md">
                <div className="flex items-center gap-3">
                    <Waves className="text-[#8d99ae]" />
                    <span className="font-bold text-lg tracking-wide uppercase">{schoolName}</span>
                </div>
                
                <div className="hidden md:flex gap-10 text-xs font-bold uppercase tracking-widest text-[#8d99ae]">
                    <a href="#" className="hover:text-white transition-colors">Vessels</a>
                    <a href="#" className="hover:text-white transition-colors">Data</a>
                    <a href="#" className="hover:text-white transition-colors">Expeditions</a>
                    <a href="#" className="hover:text-white transition-colors">Partners</a>
                </div>

                <div className="text-[10px] font-mono text-[#8d99ae] hidden md:block">
                    Depth: 0m (Surface)
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 pt-32 pb-48 px-8 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                     <div className="inline-flex items-center gap-2 border border-[#708d81] px-3 py-1 rounded-full text-[#708d81] mb-6 hover:bg-[#708d81] hover:text-[#001427] transition-colors cursor-pointer">
                         <Compass size={14} />
                         <span className="text-[10px] font-bold uppercase tracking-widest">Research Vessel Timeline</span>
                     </div>
                     
                     <h1 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
                         Exploring the <br/>
                         <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#708d81] to-[#bfd7ea]">Unknown Depths.</span>
                     </h1>
                     
                     <p className="text-[#8d99ae] text-lg leading-relaxed max-w-md mb-12">
                         Advancing human understanding of the ocean through interdisciplinary research, education, and technology.
                     </p>
                     
                     <div className="flex gap-4">
                         <Button className="rounded-full bg-[#f4d58d] hover:bg-[#e9c46a] text-[#001427] font-bold uppercase tracking-widest text-xs h-12 px-8">
                             Programs
                         </Button>
                         <Button variant="outline" className="rounded-full border-[#8d99ae] text-[#8d99ae] hover:bg-white/10 hover:text-white font-bold uppercase tracking-widest text-xs h-12 px-8 bg-transparent">
                             Live Telemetry
                         </Button>
                     </div>
                </div>

                {/* Sonar / Map Graphic */}
                <div className="relative h-[400px] w-full border border-white/10 rounded-full flex items-center justify-center bg-white/5 animate-pulse-slow">
                     <div className="absolute inset-0 rounded-full border border-white/5 scale-75"></div>
                     <div className="absolute inset-0 rounded-full border border-white/5 scale-50"></div>
                     <div className="absolute inset-0 rounded-full border border-white/5 scale-25"></div>
                     <div className="w-2 h-2 bg-[#f4d58d] rounded-full shadow-[0_0_20px_#f4d58d] animate-ping"></div>
                     
                     {/* Data Card Floating */}
                     <div className="absolute top-1/2 right-0 translate-x-1/2 bg-[#001427]/80 backdrop-blur border-l-2 border-[#f4d58d] p-4 text-[10px] font-mono text-[#8d99ae] w-48 shadow-xl">
                         <div className="mb-2 text-white font-bold uppercase border-b border-white/10 pb-1">Sensor Array 04</div>
                         <div className="flex justify-between"><span>Salinity:</span> <span>35ppt</span></div>
                         <div className="flex justify-between"><span>Temp:</span> <span>4°C</span></div>
                         <div className="flex justify-between"><span>Pressure:</span> <span>400atm</span></div>
                     </div>
                </div>
            </header>

            {/* Research Stats */}
            <section className="relative z-10 bg-[#000814] py-24 px-8 border-t border-white/5">
                <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 text-center divide-x divide-white/5">
                    {[
                         { icon: Ship, label: "Active Vessels", val: "05" },
                         { icon: Map, label: "Mapped Floor", val: "12%" },
                         { icon: Fish, label: "Species Logged", val: "50k+" },
                         { icon: Droplets, label: "Samples", val: "1.2M" },
                    ].map((item, i) => (
                        <div key={i} className="px-4 group">
                             <item.icon className="mx-auto text-[#708d81] mb-4 group-hover:scale-110 transition-transform" size={28} />
                             <div className="text-4xl font-light text-white mb-2">{item.val}</div>
                             <div className="text-[10px] font-bold uppercase tracking-widest text-[#8d99ae]">{item.label}</div>
                        </div>
                    ))}
                </div>
            </section>
            
             {/* Footer */}
            <footer className="relative z-10 bg-[#000814] pt-12 pb-20 px-8 border-t border-white/5">
                 <div className="max-w-7xl mx-auto flex flex-col items-center">
                     <p className="font-mono text-xs text-[#708d81] mb-8">
                         Lat: 34.0522 N | Lon: 118.2437 W
                     </p>
                     
                     <div className="flex gap-8 text-[10px] font-bold uppercase tracking-widest text-[#5ca4a9]">
                         <a href="#" className="hover:text-white">Publications</a>
                         <a href="#" className="hover:text-white">Careers</a>
                         <a href="#" className="hover:text-white">Contact</a>
                     </div>
                     
                     <div className="mt-12 text-[10px] text-[#8d99ae] opacity-50">
                         © 2026 {schoolName}. All systems nominal.
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default OceanographyResearch;
